import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, BUTTON_VARIANTS, Loader } from 'cdk-radial';

import AuthComponent from '../common/AuthComponent';
import Page from '../page';
import LocationsDropdownMenu from '../transaction-report/LocationsDropdownMenu';
import { mergeLocations } from '../../utils/location';
import BrowseMarketplaceButton from '../subscriptionManagement/landingPage/BrowseMarketplaceButton';
import { DealerIntegrationTable } from './DealerIntegrationTable';
import { getSubscriptions, getConnections, getAccessGrants, reset, getConnectionsForAllEntityIds } from '../../redux/connectedComponent/dealerIntegrationReport/dealerIntegrationReport.slice';
import './DealerIntegrationReport.scss';
import {createChunks} from './tableUtils';
import { PAGE_TITLE } from '../../constants/constants';
import { trackPageView, PAGE_SECTIONS, PAGE_SUBSECTIONS } from '../../utils/gtm';

const collator = new Intl.Collator(undefined, { caseFirst: 'upper' });

const LOADER_STYLE = {
  marginTop: '200px',
  marginBottom: '400px',
  display: 'flex'
};

const CHUNK_SIZE = 50;

function DealerIntegrationReport() {
  const { allLocations, authenticationToken } = useSelector(state => state.loginDetailsSlice);
  const { 
    subscriptionsByEntityId, 
    subscriptionsLoading,
    connectionsLoading,
    accessGrantsLoading, 
    hasLoadingError,
    pendingRequests,
    dealerIntegrationLoading
  } = useSelector(state => state.dealerIntegrationsReportSlice);
  const dispatch = useDispatch();

  const mergedLocations = useMemo(
    () => mergeLocations(allLocations),
    [allLocations]
  );
  const selectOptions = useMemo(
    () =>
      mergedLocations
        .map((location) => ({
          label: location.partyName,
          value: location.entityId,
        }))
        .sort((a, b) => collator.compare(a.label, b.label)),
    [mergedLocations]
  );

  const [selectedLocations, setSelectedLocations] = useState([]);

  React.useEffect(() => {
    trackPageView({
      page_section: PAGE_SECTIONS.APP_MANAGEMENT,
      page_subsection: PAGE_SUBSECTIONS.DEALER_INTEGRATION_REPORT,
    });
  }, []);

  const entityIds = useMemo(() => {
    return selectedLocations.map((loc) => loc.value);
  }, [selectedLocations]);

  const handleGenerateReport = () => {
      const entityIdChunks = createChunks(CHUNK_SIZE, entityIds);
      dispatch(reset());
      entityIdChunks?.forEach(entityIds => {
        dispatch(getConnectionsForAllEntityIds({entityIds, token: authenticationToken}));
      });
  };

  return (
    <Page pageHeader={PAGE_TITLE.APP_MANAGEMENT}>
      <div className="dealer-integrations__container">
        <div className="dealer-integrations__heading">
          <div className="transaction-report__title">
            Dealer Integration Report
          </div>
          <BrowseMarketplaceButton />
        </div>
        <div className="dealer-integrations__options">
          <LocationsDropdownMenu
            options={selectOptions}
            onChange={setSelectedLocations}
          />
          <Button
            text="Generate Report"
            variant={BUTTON_VARIANTS.PRIMARY}
            isDisabled={!selectedLocations.length}
            onClick={handleGenerateReport}
          />
        </div>
        {
          !pendingRequests &&
          !!Object.keys(subscriptionsByEntityId).length &&
            <div className="dealer-integrations__table-section">
              <DealerIntegrationTable dealerCount={entityIds.length} />
            </div>
        }
        {!!pendingRequests &&
          <Loader style={LOADER_STYLE} label="Loading..." />
        }
        {!dealerIntegrationLoading && hasLoadingError &&
          <div>
            <h1>Something went wrong.</h1>
            <h2>
              Please contact Fortellis Support at support@fortellis.io
            </h2>
          </div>
        }
      </div>
    </Page>
  );
}

export default AuthComponent(DealerIntegrationReport);